// Sound management utility for Letter Heist game

class SoundManager {
  constructor() {
    this.enabled = true;
    this.volume = 0.7;
    this.sounds = {};
    this.audioContext = null;
    
    // Initialize Web Audio API context
    this.initAudioContext();
  }

  initAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Add user interaction listener to resume audio context
      const resumeAudio = () => {
        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume().then(() => {
            console.log('Audio context resumed');
          });
        }
        // Remove listeners after first interaction
        document.removeEventListener('click', resumeAudio);
        document.removeEventListener('touchstart', resumeAudio);
        document.removeEventListener('keydown', resumeAudio);
      };
      
      document.addEventListener('click', resumeAudio);
      document.addEventListener('touchstart', resumeAudio);
      document.addEventListener('keydown', resumeAudio);
      
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
    }
  }

  // Enable/disable sounds
  setEnabled(enabled) {
    this.enabled = enabled;
  }

  // Set volume (0.0 to 1.0)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  // Generate a simple tone using Web Audio API
  generateTone(frequency, duration, type = 'sine') {
    if (!this.audioContext || !this.enabled) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
    oscillator.type = type;

    // Create envelope for smooth sound
    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(this.volume, this.audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + duration);
  }

  // Generate a chord (multiple frequencies)
  generateChord(frequencies, duration, type = 'sine') {
    if (!this.audioContext || !this.enabled) return;

    frequencies.forEach(freq => {
      this.generateTone(freq, duration, type);
    });
  }

  // Create sparkly effect with multiple high frequencies
  generateSparkle(baseFreq, duration = 0.3) {
    if (!this.audioContext || !this.enabled) return;

    // Create multiple oscillators for sparkly effect
    const frequencies = [baseFreq, baseFreq * 1.5, baseFreq * 2, baseFreq * 3];
    
    frequencies.forEach((freq, index) => {
      setTimeout(() => {
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        const filter = this.audioContext.createBiquadFilter();

        oscillator.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        oscillator.frequency.setValueAtTime(freq, this.audioContext.currentTime);
        oscillator.type = 'triangle';
        
        // High-pass filter for sparkly effect
        filter.type = 'highpass';
        filter.frequency.setValueAtTime(freq * 0.8, this.audioContext.currentTime);

        // Quick attack, fast decay for sparkle
        gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(this.volume * 0.3, this.audioContext.currentTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration * 0.3);

        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + duration * 0.3);
      }, index * 50);
    });
  }

  // Create a magical cascade effect
  generateCascade(startFreq, endFreq, steps, duration) {
    if (!this.audioContext || !this.enabled) return;

    const freqStep = (endFreq - startFreq) / steps;
    
    for (let i = 0; i < steps; i++) {
      setTimeout(() => {
        const freq = startFreq + (freqStep * i);
        this.generateTone(freq, duration, 'triangle');
        // Add sparkle to each note
        this.generateSparkle(freq * 2, duration * 0.5);
      }, i * (duration * 1000 / steps));
    }
  }

  // Create bubbly pop sound
  generateBubblePop(baseFreq) {
    if (!this.audioContext || !this.enabled) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    const filter = this.audioContext.createBiquadFilter();

    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Start high and sweep down quickly for pop effect
    oscillator.frequency.setValueAtTime(baseFreq * 3, this.audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(baseFreq, this.audioContext.currentTime + 0.1);
    oscillator.type = 'triangle';

    // Low-pass filter with resonance
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(baseFreq * 4, this.audioContext.currentTime);
    filter.frequency.exponentialRampToValueAtTime(baseFreq * 0.5, this.audioContext.currentTime + 0.15);
    filter.Q.value = 8;

    // Quick pop envelope
    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(this.volume * 0.8, this.audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.15);
  }

  // Predefined game sounds - Candy Crush style!
  playWordFound() {
    // Magical ascending cascade with sparkles
    this.generateCascade(440, 880, 6, 0.15); // A4 to A5
    
    // Add magical chord progression
    setTimeout(() => {
      this.generateChord([523.25, 659.25, 783.99], 0.6, 'triangle'); // C5, E5, G5
    }, 200);
    
    setTimeout(() => {
      this.generateChord([587.33, 739.99, 880], 0.8, 'triangle'); // D5, F#5, A5
    }, 400);

    // Sparkly finale
    setTimeout(() => {
      this.generateSparkle(1046.5, 0.5); // C6 sparkle
    }, 600);
  }

  playTileReveal() {
    // Bubbly pop with sparkle
    this.generateBubblePop(800);
    setTimeout(() => {
      this.generateSparkle(1600, 0.2);
    }, 50);
  }

  playGameWin() {
    // Epic victory fanfare with sparkles
    const victoryMelody = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    
    // Main melody
    victoryMelody.forEach((freq, index) => {
      setTimeout(() => {
        this.generateTone(freq, 0.4, 'triangle');
        this.generateSparkle(freq * 2, 0.3);
      }, index * 200);
    });

    // Triumphant chord finale
    setTimeout(() => {
      this.generateChord([523.25, 659.25, 783.99, 1046.5], 1.5, 'triangle');
      this.generateSparkle(2093, 1.0); // High sparkle
    }, 800);
  }

  playSwap() {
    // Magical swirl sound - like items swapping places
    const swapFreqs = [600, 800, 600]; // Up and back down
    
    swapFreqs.forEach((freq, index) => {
      setTimeout(() => {
        this.generateBubblePop(freq);
      }, index * 80);
    });
    
    // Add whoosh effect
    setTimeout(() => {
      this.generateSwoosh();
    }, 120);
  }

  // New! Whoosh/sweep effect
  generateSwoosh() {
    if (!this.audioContext || !this.enabled) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    const filter = this.audioContext.createBiquadFilter();

    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Sweep from high to low
    oscillator.frequency.setValueAtTime(2000, this.audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(200, this.audioContext.currentTime + 0.3);
    oscillator.type = 'triangle';

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(3000, this.audioContext.currentTime);
    filter.frequency.exponentialRampToValueAtTime(500, this.audioContext.currentTime + 0.3);

    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(this.volume * 0.3, this.audioContext.currentTime + 0.05);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.3);
  }

  // New! Special effects for future use
  playCombo(comboCount) {
    // More exciting sounds for consecutive words
    const intensity = Math.min(comboCount, 5);
    
    // Base frequency increases with combo
    const baseFreq = 440 + (intensity * 100);
    
    // More sparkles and higher pitches for higher combos
    this.generateCascade(baseFreq, baseFreq * 2, intensity + 3, 0.1);
    
    setTimeout(() => {
      this.generateSparkle(baseFreq * 3, 0.4);
    }, 200);
  }

  playSpecialWord() {
    // For particularly long or special words
    this.generateCascade(523.25, 1046.5, 8, 0.12); // Full octave cascade
    
    setTimeout(() => {
      this.generateChord([523.25, 659.25, 783.99, 987.77], 1.0, 'triangle'); // Major 7th chord
      this.generateSparkle(2093, 0.8);
    }, 300);
  }

  playLetterFall() {
    // For when new letters fall into place
    const fallFreqs = [1200, 900, 600, 400];
    
    fallFreqs.forEach((freq, index) => {
      setTimeout(() => {
        this.generateBubblePop(freq);
      }, index * 50);
    });
  }

  playGameStart() {
    // Candy Crush-style game start fanfare
    // Opening chord progression with sparkles
    
    // Start with a magical ascending chord
    const startChord = [261.63, 329.63, 392.00, 523.25]; // C-E-G-C major chord
    this.generateChord(startChord, 0.8, 'triangle');
    
    // Add sparkle effects
    setTimeout(() => {
      this.generateSparkle(659.25, 0.6); // E note sparkle
    }, 150);
    
    setTimeout(() => {
      this.generateSparkle(783.99, 0.6); // G note sparkle  
    }, 300);
    
    // Triumphant ascending cascade
    setTimeout(() => {
      this.generateCascade(523.25, 1046.5, 6, 0.15); // Full octave C to C
    }, 450);
    
    // Final magical shimmer
    setTimeout(() => {
      this.generateSparkle(1318.5, 0.8); // High E
      this.generateBubblePop(2093); // Very high C
    }, 800);
    
    // Add a subtle swoosh for the "ready to play" feeling
    setTimeout(() => {
      this.generateSwoosh(1500, 600, 0.3);
    }, 1000);
  }

  playLobbyButtonClick() {
    // A quick, bright chord for a cheerful confirmation
    const chord = [523.25, 659.25, 783.99]; // C Major chord
    this.generateChord(chord, 0.2, 'triangle');
    setTimeout(() => {
      this.generateSparkle(1046.5, 0.2);
    }, 50);
  }

  playStartPageWelcome() {
    // Debug logging
    if (!this.audioContext) {
      console.warn('No audio context available');
      return;
    }
    if (!this.enabled) {
      console.warn('Sounds are disabled');
      return;
    }
    if (this.audioContext.state === 'suspended') {
      console.warn('Audio context is suspended - need user interaction');
      return;
    }
    
    console.log('Playing welcome sound...');
    
    // Playful welcome melody - like opening a candy box!
    // A gentle ascending melody with sparkles
    const welcomeMelody = [392.00, 440.00, 493.88, 523.25]; // G-A-B-C
    
    // Play the main melody notes
    welcomeMelody.forEach((freq, index) => {
      setTimeout(() => {
        this.generateTone(freq, 0.4, 'triangle');
        // Add gentle sparkles on each note
        setTimeout(() => {
          this.generateSparkle(freq * 2, 0.2);
        }, 100);
      }, index * 200);
    });
    
    // Add a cheerful finishing chord after the melody
    setTimeout(() => {
      const finishChord = [523.25, 659.25, 783.99]; // C Major
      this.generateChord(finishChord, 0.6, 'triangle');
      // Final sparkle flourish
      setTimeout(() => {
        this.generateSparkle(1046.5, 0.4);
      }, 200);
    }, 900);
  }
}

// Create and export a singleton instance
export const soundManager = new SoundManager();

// Export individual sound functions for convenience
export const playWordFoundSound = () => soundManager.playWordFound();
export const playTileRevealSound = () => soundManager.playTileReveal();
export const playGameWinSound = () => soundManager.playGameWin();
export const playSwapSound = () => soundManager.playSwap();
export const playLobbyButtonClickSound = () => soundManager.playLobbyButtonClick();
export const playStartPageWelcomeSound = () => soundManager.playStartPageWelcome();

// New Candy Crush style sounds
export const playComboSound = (comboCount) => soundManager.playCombo(comboCount);
export const playSpecialWordSound = () => soundManager.playSpecialWord();
export const playLetterFallSound = () => soundManager.playLetterFall();
export const playGameStartSound = () => soundManager.playGameStart();

export default soundManager;